import Cookies from 'js-cookie';
import L from 'leaflet';
import 'leaflet.vectorgrid';
import { useEffect } from 'react';
import { useMap } from 'react-leaflet';

type TMapAzureLiveTraffic = {
  nexisUrl?: string;
};

export const MapAzureLiveTraffic = ({ nexisUrl }: TMapAzureLiveTraffic) => {
  const map = useMap();
  const authToken = Cookies.get('authToken');

  useEffect(() => {
    if (!map) return;

    // Don't load traffic layer if nexisUrl is not provided or empty
    if (!nexisUrl) {
      return;
    }

    const AZURE_TRAFFIC_CONFIG = {
      BASE_URL: nexisUrl,
      API_PATH: '/api/maps/azure/traffic/flow/tile',
      STYLE: 'relative',
    };

    // Prefer vector tiles (customizable) via leaflet.vectorgrid; fallback to raster PNG tiles if unavailable
    let cleanup: (() => void) | undefined;
    const paneName = 'trafficPane';
    // Ensure a dedicated pane with higher z-index than base tiles
    if (!map.getPane(paneName)) {
      const pane = map.createPane(paneName);
      pane.style.zIndex = '200'; // above overlayPane and tilePane, below markers
      pane.style.pointerEvents = 'none'; // keep interactions on markers/controls below
    }

    const attachVectorTiles = async () => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const LAny = L as any;
        if (!LAny.vectorGrid || !LAny.vectorGrid.protobuf) {
          throw new Error('VectorGrid protobuf not available');
        }

        const url = `${AZURE_TRAFFIC_CONFIG.BASE_URL}${
          AZURE_TRAFFIC_CONFIG.API_PATH
        }?z={z}&x={x}&y={y}&style=${AZURE_TRAFFIC_CONFIG.STYLE}${
          authToken ? `&token=${encodeURIComponent(authToken)}` : ''
        }`;

        // Preferred: weight primarily by congestion, lightly adjusted by zoom
        const getStrokeWeight = (lv: number, z: number) => {
          // Base by congestion (slow -> thicker)
          const base = lv < 0.4 ? 2.6 : lv < 0.6 ? 2.0 : lv < 0.8 ? 1.5 : 1.2;
          // Gentle zoom multiplier
          const mult = z >= 17 ? 1.2 : z >= 14 ? 1.12 : z >= 12 ? 1.06 : 1.0;
          const w = base * mult;
          return Math.max(1.0, Math.min(3.4, w));
        };

        // Main colored line style
        const styleFn = (properties: Record<string, unknown>) => {
          const level =
            (properties?.traffic_level as number | undefined) ??
            (properties?.trafficLevel as number | undefined) ??
            (properties?.level as number | undefined);
          const lv =
            typeof level === 'number' ? Math.max(0, Math.min(1, level)) : 0.6;
          let color = '#21ba45';
          if (lv < 0.4) color = '#d32f2f';
          else if (lv < 0.6) color = '#ff8c00';
          else if (lv < 0.8) color = '#f2c037';
          const weight = getStrokeWeight(lv, map.getZoom());
          return {
            color,
            weight,
            opacity: 0.9,
            lineCap: 'round',
            lineJoin: 'round',
          };
        };

        // Subtle white casing under main line for contrast
        const styleFnCasing = (properties: Record<string, unknown>) => {
          const level =
            (properties?.traffic_level as number | undefined) ??
            (properties?.trafficLevel as number | undefined) ??
            (properties?.level as number | undefined);
          const lv =
            typeof level === 'number' ? Math.max(0, Math.min(1, level)) : 0.6;
          const weight = getStrokeWeight(lv, map.getZoom()) + 0.8;
          return {
            color: '#ffffff',
            weight,
            opacity: 0.55,
            lineCap: 'round',
            lineJoin: 'round',
          };
        };
        // Proxy returns styleFn for any layer name key
        const vectorTileLayerStyles = new Proxy(
          {},
          {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            get: (_target, _prop) => styleFn,
          }
        );
        const vectorTileLayerStylesCasing = new Proxy(
          {},
          {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            get: (_target, _prop) => styleFnCasing,
          }
        );

        const casingLayer = LAny.vectorGrid.protobuf(url, {
          maxNativeZoom: 22,
          maxZoom: 22,
          pane: paneName,
          vectorTileLayerStyles: vectorTileLayerStylesCasing,
        });
        const vectorLayer = LAny.vectorGrid.protobuf(url, {
          maxNativeZoom: 22,
          maxZoom: 22,
          pane: paneName,
          vectorTileLayerStyles,
        });

        casingLayer.addTo(map);
        vectorLayer.addTo(map).bringToFront();
        cleanup = () => {
          map.removeLayer(casingLayer);
          map.removeLayer(vectorLayer);
        };
      } catch (error) {
        // Fallback to raster PNG tiles (cannot custom style, but ensures functionality)
        const trafficTiles = L.tileLayer(
          `${AZURE_TRAFFIC_CONFIG.BASE_URL}${
            AZURE_TRAFFIC_CONFIG.API_PATH
          }?z={z}&x={x}&y={y}&style=${AZURE_TRAFFIC_CONFIG.STYLE}${
            authToken ? `&token=${encodeURIComponent(authToken)}` : ''
          }`,
          {
            opacity: 0.85,
            tileSize: 256,
            pane: paneName,
          }
        ).addTo(map);
        console.log(error, 'error');
        trafficTiles.bringToFront();
        cleanup = () => {
          map.removeLayer(trafficTiles);
        };
      }
    };

    attachVectorTiles();

    return () => {
      if (cleanup) cleanup();
    };
  }, [map, nexisUrl, authToken]);

  return null;
};
